import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import ImageCropModal from "./ImageCropModal";

/**
 * Profile picture upload: file picker → crop/rotate/zoom modal → save as data URL.
 * onChange(dataUrl) fires only after the user confirms in the crop modal.
 */
export default function ProfilePictureUpload({ value, onChange, testId = "picture-upload", label = "Upload profile picture" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [srcUrl, setSrcUrl] = useState(null);
  const [open, setOpen] = useState(false);

  const pick = () => ref.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8 MB"); return; }
    setBusy(true);
    try {
      const url = URL.createObjectURL(file);
      setSrcUrl(url);
      setOpen(true);
    } finally { setBusy(false); }
  };

  const handleSave = (dataUrl) => {
    onChange(dataUrl);
    if (srcUrl) URL.revokeObjectURL(srcUrl);
    setSrcUrl(null);
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-24 h-24 rounded-full bg-slate-100 grid place-items-center overflow-hidden border border-slate-200">
        {value ? (
          <img src={value} alt="preview" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <Camera className="w-6 h-6 text-slate-400" />
        )}
      </div>
      <div>
        <button type="button" onClick={pick} disabled={busy} data-testid={testId}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          {value ? "Replace" : label}
        </button>
        <div className="text-xs text-slate-500 mt-1">JPG or PNG • You can crop and rotate before saving.</div>
        <input ref={ref} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`${testId}-input`} />
      </div>
      <ImageCropModal open={open} onOpenChange={setOpen} sourceUrl={srcUrl} onSave={handleSave} />
    </div>
  );
}
